import { Component } from '@theme/component';

/* Must mirror the CSS breakpoint that shows .site-header__menu-toggle
   (@media (max-width: 800px)): from 801px up the hamburger is gone, so an
   open drawer could never be closed by the customer again. */
const DESKTOP_QUERY = '(min-width: 801px)';

export class HeaderDrawerComponent extends Component {
  requiredRefs = ['dialog'];

  #shell = null;
  #desktopMql = null;

  connectedCallback() {
    super.connectedCallback();
    this.#shell = this.closest('[data-site-header]');
    const { dialog } = this.refs;
    if (dialog) {
      dialog.addEventListener('close', this.#handleNativeClose);
      dialog.addEventListener('click', this.#handleDialogClick);
    }
    this.addEventListener('dialog:open', this.#onOpen);
    this.addEventListener('dialog:close', this.#onClose);
    document.addEventListener('site:navigation:start', this.#onNavigationStart);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { dialog } = this.refs;
    if (dialog) {
      dialog.removeEventListener('close', this.#handleNativeClose);
      dialog.removeEventListener('click', this.#handleDialogClick);
    }
    this.#unwatchDesktop();
    this.removeEventListener('dialog:open', this.#onOpen);
    this.removeEventListener('dialog:close', this.#onClose);
    document.removeEventListener('site:navigation:start', this.#onNavigationStart);
  }

  showDialog() {
    const { dialog } = this.refs;
    if (!dialog || dialog.open) return;
    /* Same feature-detected open as dialog.js: browsers without native
       showModal fall back to a plain `open` attribute. The dialog.open guard
       above keeps an already-open modal from being re-shown. */
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    this.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
  }

  closeDialog() {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open) return;
    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
  }

  handleCloseClick(event) {
    this.closeDialog();
  }

  #onOpen = () => {
    this.#shell?.classList.add('is-menu-open');
    this.#setTogglesExpanded(true);
    document.body.classList.add('site-header-drawer-open');
    this.#watchDesktop();
    // The drawer's <dialog> lives inside the transformed header shell, so
    // force the chrome back into view — a retracted (translated) shell
    // would shift the dialog out of the viewport. release() on close lets
    // scroll position drive the chrome state again.
    window.ThemeChrome?.show?.();
  };

  #onClose = () => {
    this.#shell?.classList.remove('is-menu-open');
    this.#setTogglesExpanded(false);
    document.body.classList.remove('site-header-drawer-open');
    this.#unwatchDesktop();
    /* The drawer DOM persists across soft navigations, so any <details>
       group the customer expanded must not leak into the next visit —
       collapse them on every close (manual, Esc or navigation-driven) so
       the drawer always reopens fully folded. */
    this.querySelectorAll('details[open]').forEach(function (details) {
      details.removeAttribute('open');
    });
    window.ThemeChrome?.release?.();
  };

  /* Light dismiss. Unlike DialogComponent's rect-based backdrop check this
     dialog fills the viewport (its ::backdrop has zero area), so a click on
     the overlay surface lands on the <dialog> element itself while clicks
     inside the drawer land within the panel. Anything outside the panel is
     an outside click — close. */
  #handleDialogClick = (event) => {
    const { dialog, panel } = this.refs;
    if (!dialog || !dialog.open) return;
    if (panel && panel.contains(event.target)) return;
    if (event.target !== dialog) return;
    this.closeDialog();
  };

  /* A modal <dialog> opened below the desktop breakpoint keeps rendering —
     and keeps the body scroll lock — after the viewport grows past the
     hamburger's breakpoint. While the drawer is open, watch the desktop
     breakpoint and route the close through the regular dialog.close() path
     so aria-expanded, the body class and the chrome release all reset.
     The watcher is removed on close, so shrinking back down never re-opens
     the drawer. */
  #watchDesktop() {
    if (this.#desktopMql) return;
    this.#desktopMql = window.matchMedia(DESKTOP_QUERY);
    this.#desktopMql.addEventListener('change', this.#onDesktopChange);
  }

  #unwatchDesktop() {
    if (!this.#desktopMql) return;
    this.#desktopMql.removeEventListener('change', this.#onDesktopChange);
    this.#desktopMql = null;
  }

  #onDesktopChange = (event) => {
    if (event.matches) this.closeDialog();
  };

  /** Keep every hamburger that opens this drawer (the header's own and the
      product-subnav's fallback on takeover pages) in sync. */
  #setTogglesExpanded(expanded) {
    document.querySelectorAll('[data-header-menu-toggle]').forEach(function (toggle) {
      toggle.setAttribute('aria-expanded', String(expanded));
    });
  }

  /* The drawer DOM persists across soft navigations (the header chrome is
     shared between pages), so a link tapped inside the open drawer swaps
     #MainContent while the dialog and its body scroll lock stay active.
     Mirror cart-drawer: close on navigation:start — the native `close`
     event drives the regular #onClose path (body class + chrome release),
     so the scroll lock is cleared exactly like a manual close. */
  #onNavigationStart = () => {
    if (this.refs.dialog?.open) this.closeDialog();
  };

  #handleNativeClose = () => {
    this.dispatchEvent(new CustomEvent('dialog:close', { bubbles: true }));
  };
}

if (!customElements.get('header-drawer-component')) {
  customElements.define('header-drawer-component', HeaderDrawerComponent);
}
